import React, { useState, useMemo } from "react";
import { useMutation } from "@apollo/client";
import EventDateTimeInput from "../EventDateTimeInput/EventDateTimeInput";
import {
  ADD_MANUAL_TIME_ENTRY,
  EDIT_TIME_ENTRY,
} from "../../../../utils/GraphQL/mutations";
import "./editTimeEntryModal.css";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";
import BdrCheckbox from "../../../../components/BdrCheckbox/BdrCheckbox";

const ONE_HOUR_MS = 3_600_000;

// Shared time-entry modal used by:
//   1. Payroll Report (lockedDate set → date pinned to entry's day)
//   2. Timesheet page (lockedDate null → date editable, allowOpenShift true
//      so the "Leave shift open" checkbox renders in edit mode)
//
// Concurrency note: the server's editTimeEntry uses findByIdAndUpdate
// (last-write-wins). At most 1-2 admins ever edit time entries and never
// at the same time, so this is acceptable per user.
const EditTimeEntryModal = ({
  mode,                    // "create" | "edit"
  userId,                  // required in create mode (taken from initial in edit)
  initial,                 // { entryId, clockInMs, clockOutMs, source } — required in edit
  lockedDate = null,       // Date | null. Non-null → date pinned (payroll page).
  allowOpenShift = false,  // when true + edit mode, shows "Leave shift open" checkbox
  currentPeriodStart,      // epoch ms string — for past-period warning + period membership flag
  currentPeriodEnd,        // epoch ms string — for period membership flag returned to parent
  onClose,
  onSaved,                 // ({ entry, wasInDisplayedPeriod })
}) => {
  const [clockInMs, setClockInMs] = useState(
    mode === "edit" ? initial?.clockInMs || "" : ""
  );
  const [clockOutMs, setClockOutMs] = useState(
    mode === "edit" ? initial?.clockOutMs || "" : ""
  );
  const [keepOpen, setKeepOpen] = useState(
    mode === "edit" && allowOpenShift && initial?.clockOutMs == null
  );
  const [confirmingSave, setConfirmingSave] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [addManual, { loading: adding }] = useMutation(ADD_MANUAL_TIME_ENTRY, {
    onCompleted: (data) => {
      const entry = data?.addManualTimeEntry;
      const inMs = parseInt(entry?.clockInMs || "0", 10);
      const wasInDisplayedPeriod =
        currentPeriodStart != null &&
        currentPeriodEnd != null &&
        inMs >= parseInt(currentPeriodStart, 10) &&
        inMs <= parseInt(currentPeriodEnd, 10);
      onSaved && onSaved({ entry, wasInDisplayedPeriod });
      onClose && onClose();
    },
    onError: (err) => setErrorMessage(err.message || "Couldn't create entry."),
  });

  const [editEntry, { loading: editing }] = useMutation(EDIT_TIME_ENTRY, {
    onCompleted: (data) => {
      const entry = data?.editTimeEntry;
      const inMs = parseInt(entry?.clockInMs || "0", 10);
      const wasInDisplayedPeriod =
        currentPeriodStart != null &&
        currentPeriodEnd != null &&
        inMs >= parseInt(currentPeriodStart, 10) &&
        inMs <= parseInt(currentPeriodEnd, 10);
      onSaved && onSaved({ entry, wasInDisplayedPeriod });
      onClose && onClose();
    },
    onError: (err) => setErrorMessage(err.message || "Couldn't save entry."),
  });

  const busy = adding || editing;

  // Past-period warning fires when the entry's clock-in falls before the
  // currently-displayed pay period. This is the only place admins get a
  // visible hint that they're editing closed-period payroll history.
  const isPastPeriod = useMemo(() => {
    if (!currentPeriodStart || !clockInMs) return false;
    return parseInt(clockInMs, 10) < parseInt(currentPeriodStart, 10);
  }, [clockInMs, currentPeriodStart]);

  // Smart time-sync (free-date mode only — `lockedDate === null` AND
  // `!keepOpen`). Mirrors CreateEventModal's start/end pattern but with
  // a 1-hour gap (vs. calendar events' 2-hour). In locked-date mode the
  // dates are pinned and a push could create cross-midnight value/display
  // disagreement, so we skip the sync there and let the save validator
  // catch any clock-out ≤ clock-in case.
  const shouldSyncTimes = !lockedDate && !keepOpen;

  const handleClockInChange = (next) => {
    if (typeof next !== "string") {
      setClockInMs(next);
      return;
    }
    const newStart = parseInt(next, 10);
    if (isNaN(newStart) || !shouldSyncTimes) {
      setClockInMs(next);
      return;
    }
    setClockInMs(next);
    if (!clockOutMs) {
      // First-time clock-in pick: auto-fill clock-out 1 hour later so
      // both fields populate from a single date click. Admin can still
      // override either field independently.
      setClockOutMs(String(newStart + ONE_HOUR_MS));
      return;
    }
    const oldEnd = parseInt(clockOutMs, 10);
    if (isNaN(oldEnd)) return;
    // Move clock-out to the same calendar day as the new clock-in,
    // keeping its hours/minutes/seconds.
    const startDt = new Date(newStart);
    const endDt = new Date(oldEnd);
    const syncedEnd = new Date(
      startDt.getFullYear(), startDt.getMonth(), startDt.getDate(),
      endDt.getHours(), endDt.getMinutes(), endDt.getSeconds()
    ).getTime();
    if (newStart >= syncedEnd) {
      // Pushing clock-in forward past clock-out — bump clock-out to
      // start + 1hr so they don't cross.
      setClockOutMs(String(newStart + ONE_HOUR_MS));
    } else if (syncedEnd !== oldEnd) {
      // Date changed (but time order is fine) — just sync the date.
      setClockOutMs(String(syncedEnd));
    }
  };

  const handleClockOutChange = (next) => {
    if (typeof next !== "string") {
      setClockOutMs(next);
      return;
    }
    const newEnd = parseInt(next, 10);
    if (isNaN(newEnd) || !shouldSyncTimes) {
      setClockOutMs(next);
      return;
    }
    if (clockInMs) {
      const curStart = parseInt(clockInMs, 10);
      if (!isNaN(curStart) && newEnd <= curStart) {
        // Pulling clock-out back to or before clock-in — pull clock-in
        // back to end - 1hr so they don't cross.
        setClockInMs(String(newEnd - ONE_HOUR_MS));
      }
    }
    setClockOutMs(next);
  };

  const canSave = useMemo(() => {
    if (!clockInMs) return false;
    if (keepOpen) return true; // open shift — clockOut not required
    if (!clockOutMs) return false;
    return parseInt(clockOutMs, 10) > parseInt(clockInMs, 10);
  }, [clockInMs, clockOutMs, keepOpen]);

  const handleSaveClick = () => {
    setErrorMessage(null);
    if (!clockInMs) {
      setErrorMessage("Clock in is required.");
      return;
    }
    if (!keepOpen) {
      if (!clockOutMs) {
        setErrorMessage("Clock out is required.");
        return;
      }
      if (parseInt(clockOutMs, 10) <= parseInt(clockInMs, 10)) {
        setErrorMessage(
          "Clock out must be after clock in. (If this entry spans midnight, edit it on the Timesheet page.)"
        );
        return;
      }
    }
    setConfirmingSave(true);
  };

  const handleConfirmSave = () => {
    setConfirmingSave(false);
    if (mode === "edit") {
      editEntry({
        variables: {
          entryId: initial.entryId,
          clockInMs,
          clockOutMs: keepOpen ? null : clockOutMs,
        },
      });
    } else {
      addManual({
        variables: { userId, clockInMs, clockOutMs },
      });
    }
  };

  return (
    <>
      <BdrModal
        open
        onClose={onClose}
        size="sm"
        variant="panel"
        closeOnBackdrop={!busy}
        closeOnEsc={!busy}
        title={mode === "edit" ? "Edit Time Entry" : "Add Manual Entry"}
        actions={
          <>
            <BdrButton onClick={onClose} disabled={busy}>
              Cancel
            </BdrButton>
            <BdrButton
              variant="confirm-solid"
              onClick={handleSaveClick}
              disabled={!canSave || busy}
            >
              {mode === "edit" ? "Save Changes" : "Add Entry"}
            </BdrButton>
          </>
        }
      >
        {isPastPeriod && (
          <div className="bdr-alert bdr-alert--warning bdr-alert--start">
            Editing a time entry from a previous pay period will change
            historical paycheck totals.
          </div>
        )}

        {errorMessage && (
          <div className="bdr-alert bdr-alert--danger bdr-alert--start" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="bdr-field">
          <span className="bdr-field-label">Clock In</span>
          <EventDateTimeInput
            value={clockInMs}
            onChange={handleClockInChange}
            label=""
            minuteStep={1}
            hourRange={[0, 23]}
            lockedDate={lockedDate}
          />
        </div>

        <div className="bdr-field">
          <span className="bdr-field-label">Clock Out</span>
          {keepOpen ? (
            <div className="tem-open-shift-display">
              — open shift (no clock-out) —
            </div>
          ) : (
            <EventDateTimeInput
              value={clockOutMs}
              onChange={handleClockOutChange}
              label=""
              minuteStep={1}
              hourRange={[0, 23]}
              lockedDate={lockedDate}
            />
          )}
        </div>

        {allowOpenShift && mode === "edit" && (
          <BdrCheckbox
            label="Leave shift open (no clock-out yet)"
            checked={keepOpen}
            onChange={(ev) => setKeepOpen(ev.target.checked)}
            disabled={busy}
          />
        )}
      </BdrModal>

      {/*
        A real modal at the next rung, not an overlay painted inside the first
        one. It cannot be BdrButton's built-in confirm gate either: that fires
        `onClick` only AFTER the user confirms, and `handleSaveClick` VALIDATES
        first — you would confirm a save and then be told the clock-out is
        missing.
      */}
      <BdrModal
        open={confirmingSave}
        onClose={() => setConfirmingSave(false)}
        size="sm"
        layer="raised"
        variant="panel"
        title={mode === "edit" ? "Save changes?" : "Create this entry?"}
        actions={
          <>
            <BdrButton onClick={() => setConfirmingSave(false)} disabled={busy}>
              Cancel
            </BdrButton>
            <BdrButton
              variant="confirm-solid"
              onClick={handleConfirmSave}
              disabled={busy}
            >
              Confirm
            </BdrButton>
          </>
        }
      >
        <p className="bdr-confirm-note">
          {mode === "edit"
            ? "Save changes to this time entry?"
            : "Create this time entry?"}
          {isPastPeriod &&
            " This entry is in a previous pay period, so a historical paycheck total will change."}
        </p>
      </BdrModal>
    </>
  );
};

export default EditTimeEntryModal;
